import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../utils/api';
import { formatCurrency } from '../utils/formatters';

const ClientPortal = () => {
    const { user } = useAuth();
    const [invoices, setInvoices] = useState([]);
    const [payments, setPayments] = useState([]);
    const [paidInvoices, setPaidInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('invoices');

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [invoicesRes, paymentsRes] = await Promise.all([
                apiClient.get('/client-portal/invoices'),
                apiClient.get('/client-portal/payments'),
            ]);
            setInvoices(invoicesRes.data);
            setPayments(paymentsRes.data.transactions || []);
            setPaidInvoices(paymentsRes.data.paid_invoices || []);
        } catch (error) {
            console.error('Error fetching client portal data:', error);
        } finally {
            setLoading(false);
        }
    };

    const statusColor = (status) => {
        if (status === 'paid') return 'bg-green-100 text-green-800';
        if (status === 'overdue') return 'bg-red-100 text-red-800';
        if (status === 'sent') return 'bg-blue-100 text-blue-800';
        return 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-gray-500">Loading your portal...</div>
            </div>
        );
    }

    return (
        <div className="p-8 max-w-5xl mx-auto">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-gray-900">Client Portal</h1>
                <p className="text-gray-500 mt-1">Welcome back, {user?.name || user?.email}</p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Total Invoices</p>
                    <p className="text-3xl font-bold text-gray-900">{invoices.length}</p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Outstanding</p>
                    <p className="text-3xl font-bold text-orange-600">
                        {invoices.filter(i => i.status === 'sent' || i.status === 'overdue').length}
                    </p>
                </div>
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <p className="text-sm text-gray-500 mb-1">Paid</p>
                    <p className="text-3xl font-bold text-green-600">
                        {invoices.filter(i => i.status === 'paid').length}
                    </p>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex space-x-1 mb-6 bg-gray-100 rounded-lg p-1 w-fit">
                <button
                    onClick={() => setActiveTab('invoices')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'invoices' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    My Invoices
                </button>
                <button
                    onClick={() => setActiveTab('payments')}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${activeTab === 'payments' ? 'bg-white shadow text-blue-600' : 'text-gray-500 hover:text-gray-700'
                        }`}
                >
                    Payment History
                </button>
            </div>

            {/* Invoices Tab */}
            {activeTab === 'invoices' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {invoices.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="text-5xl mb-4">📄</div>
                            <p className="text-gray-500">No invoices found for your account.</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Issue Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {invoices.map((invoice) => (
                                    <tr key={invoice.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{invoice.invoice_number}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{invoice.issue_date}</td>
                                        <td className="px-6 py-4 text-sm text-gray-600">{invoice.due_date}</td>
                                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                                            {formatCurrency(invoice.total, invoice.currency)}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full font-medium ${statusColor(invoice.status)}`}>
                                                {invoice.status}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}

            {/* Payments Tab */}
            {activeTab === 'payments' && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {paidInvoices.length === 0 ? (
                        <div className="text-center py-16">
                            <div className="text-5xl mb-4">💳</div>
                            <p className="text-gray-500">No payment history yet.</p>
                        </div>
                    ) : (
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date Paid</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {paidInvoices.map((inv) => (
                                    <tr key={inv.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{inv.invoice_number}</td>
                                        <td className="px-6 py-4 text-sm font-semibold text-gray-900">
                                            {formatCurrency(inv.total, inv.currency)}
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600">
                                            {inv.paid_at ? new Date(inv.paid_at).toLocaleDateString() : '—'}
                                        </td>
                                        <td className="px-6 py-4">
                                            <span className="px-2 py-1 text-xs rounded-full font-medium bg-green-100 text-green-800">
                                                Paid
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
};

export default ClientPortal;
